<template>
  <div>
    <b-table
      responsive="sm"
      :fields="fields"
      :items="dataList"
      striped
      :busy="loading"
      :empty-text="emptyText"
      :show-empty="!!emptyText"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle" />
          <strong class="ml-2">{{ $t('common.loading') }}</strong>
        </div>
      </template>
      <template #cell(title)="data">
        <div class="font-weight-bold">
          {{ data.item.title }}
        </div>
        <div class="text-primary font-small-3">
          <strong>{{ $t('projects.table.department') }}</strong> {{ data.item.department }}
        </div>
        <div class="text-danger font-small-3">
          <strong>{{ $t('projects.table.team') }}</strong> {{ data.item.team }}
        </div>
      </template>
      <template #cell(description)="data">
        <span>{{ truncate(data.item.description, 100) }}</span>
      </template>
      <template #cell(target_date)="data">
        <span>{{ formatDate(data.item.target_date) }}</span>
      </template>
      <template #cell(completed_date)="data">
        <span>{{ formatDate(data.item.completed_date) }}</span>
      </template>
      <template #cell(modified)="data">
        <span>{{ formatDate(data.item.modified) }}</span>
      </template>
      <template #cell(username)="data">
        <div class="font-weight-bold border-bottom pb-1 mb-1">
          {{ data.item.username }}
        </div>
        <div class="font-small-3 text-success d-flex align-items-center">
          <FeatherIcon
            icon="PlusCircleIcon"
            style="margin-right: 4px"
          /> {{ formatDate(data.item.modified) }}
        </div>
        <div class="font-small-3 text-warning d-flex align-items-center">
          <FeatherIcon
            icon="EditIcon"
            style="margin-right: 4px"
          /> {{ formatDate(data.item.modified) }}
        </div>
      </template>
      <template #cell(cancelled_date)="data">
        <span>{{ formatDate(data.item.cancelled_date) }}</span>
      </template>
      <template #cell(created)="data">
        <span>{{ formatDate(data.item.created) }}</span>
      </template>
      <template #cell(task_progress)="data">
        <div style="min-width: 120px;">
          <b-progress
            :value="data.item.taskCount > 0 ? Math.round((data.item.completedTaskCount / data.item.taskCount) * 100) : 0"
            max="100"
            height="6px"
            variant="success"
          />
          <small class="text-muted">{{ data.item.completedTaskCount }} / {{ data.item.taskCount }}</small>
        </div>
      </template>
      <template #cell(control)="data">
        <div class="d-flex justify-content-end">
          <list-buttons
            :view-action="'/app/projects/detail/' + data.item.id"
            :edit-action="'/app/projects/edit/' + data.item.id"
            :delete-action="canWriteProject(data.item) ? removeItem : null"
            :hide-edit="statusFilter !== 'open' || !canWriteProject(data.item)"
            :data-id="String(data.item.id)"
          >
            <template #extra-items>
              <template v-if="statusFilter === 'open' && canWriteProject(data.item)">
                <b-dropdown-item @click="$emit('complete', data.item)">
                  <feather-icon icon="CheckIcon" />
                  <span class="align-middle ml-50">{{ $t('projects.table.complete') }}</span>
                </b-dropdown-item>
                <b-dropdown-item @click="$emit('cancel', data.item)">
                  <feather-icon icon="XCircleIcon" />
                  <span class="align-middle ml-50">{{ $t('projects.table.cancel') }}</span>
                </b-dropdown-item>
              </template>
            </template>
          </list-buttons>
        </div>
      </template>
    </b-table>
    <b-card-footer v-if="dataCounts > perPage">
      <b-pagination
        :value="currentPage"
        :total-rows="dataCounts"
        :per-page="perPage"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
        @change="onPageChange"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </template>
      </b-pagination>
    </b-card-footer>
  </div>
</template>

<script>
import { canWriteProject } from '@/auth/taskPermissions'
import {
  BTable, BSpinner, BDropdownItem, BCardFooter, BPagination, BProgress,
} from 'bootstrap-vue'
import ListButtons from '@/layouts/components/common/ListButtons.vue'

export default {
  name: 'ProjectsTable',
  components: {
    BTable,
    BSpinner,
    BDropdownItem,
    BCardFooter,
    BPagination,
    BProgress,
    ListButtons,
  },
  props: {
    dataList: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: null,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 12,
    },
    dataCounts: {
      type: Number,
      default: 0,
    },
    statusFilter: {
      type: String,
      default: 'open',
    },
  },
  computed: {
    fields() {
      const titleCol = { key: 'title', label: this.$t('fields.title') }
      const controlCol = { key: 'control', label: '', thClass: 'width-200 text-nowrap' }
      // const departmentCol = { key: 'department', label: 'Departman', thClass: 'width-200 text-nowrap' }
      // const teamCol = { key: 'team', label: 'Ekip', thClass: 'width-200 text-nowrap' }
      const creatorCol = { key: 'username', label: this.$t('projects.table.createdBy'), thClass: 'width-200 text-nowrap' }

      if (this.statusFilter === 'completed') {
        return [
          titleCol,
          { key: 'completed_date', label: this.$t('projects.table.completedDate'), thClass: 'width-200 text-nowrap' },
          // departmentCol,
          // teamCol,
          creatorCol,
          controlCol,
        ]
      }
      if (this.statusFilter === 'cancelled') {
        return [
          titleCol,
          { key: 'cancelled_date', label: this.$t('projects.table.cancelledDate'), thClass: 'width-200 text-nowrap' },
          // departmentCol,
          // teamCol,
          creatorCol,
          controlCol,
        ]
      }
      return [
        titleCol,
        { key: 'target_date', label: this.$t('fields.targetDate'), thClass: 'width-200 text-nowrap' },
        // departmentCol,
        // teamCol,
        creatorCol,
        { key: 'task_progress', label: this.$t('projects.table.taskProgress'), thClass: 'width-200 text-nowrap' },
        controlCol,
      ]
    },
  },
  methods: {
    canWriteProject,
    truncate(str, len) {
      if (!str) return '-'
      return str.length > len ? `${str.substring(0, len)}...` : str
    },
    formatDate(val) {
      if (!val) return '-'
      return this.moment(val).format('DD.MM.YYYY')
    },
    onPageChange(page) {
      this.$emit('update:currentPage', page)
    },
    removeItem(id) {
      this.$emit('remove', Number(id))
    },
  },
}
</script>
